export const TEAM_CELEBRATION_TIME_ZONE = "Asia/Almaty";

export type TeamCelebrationKind = "birthday" | "anniversary";

export type TeamCelebration = {
  key: string;
  userId: number;
  kind: TeamCelebrationKind;
  name: string;
  picture: string | null;
  years: number | null;
  title: string;
  message: string;
  dismissed: boolean;
};

export type CelebrationPerson = {
  userId: number;
  name: string | null;
  picture: string | null;
  birthdayMonth: number | null;
  birthdayDay: number | null;
  startedOn: string | null;
  greetingName?: string | null;
};

export type TeamCelebrationDateInput = {
  birthdayMonth: number | null;
  birthdayDay: number | null;
  startedOn: string | null;
  greetingName: string | null;
};

export function almatyCalendarDate(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TEAM_CELEBRATION_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function isValidCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

export function validateCelebrationDates(
  input: TeamCelebrationDateInput,
  today = almatyCalendarDate()
): string | null {
  const hasMonth = input.birthdayMonth !== null;
  const hasDay = input.birthdayDay !== null;
  if (hasMonth !== hasDay) return "Укажите месяц и день рождения вместе";
  if (hasMonth) {
    const month = input.birthdayMonth!;
    const day = input.birthdayDay!;
    const probe = new Date(Date.UTC(2000, month - 1, day));
    if (
      !Number.isInteger(month) ||
      !Number.isInteger(day) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      probe.getUTCMonth() !== month - 1 ||
      probe.getUTCDate() !== day
    ) {
      return "Укажите существующую дату рождения";
    }
  }
  if (input.startedOn !== null) {
    if (!isValidCalendarDate(input.startedOn)) {
      return "Укажите дату начала работы в формате ГГГГ-ММ-ДД";
    }
    if (input.startedOn > today)
      return "Дата начала работы не может быть в будущем";
  }
  if (input.greetingName !== null && input.greetingName.trim().length > 80) {
    return "Имя для поздравления — не длиннее 80 символов";
  }
  return null;
}

export function celebrationKey(
  date: string,
  kind: TeamCelebrationKind,
  userId: number
): string {
  return `${date}:${kind}:${userId}`;
}

function stableChoice(seed: string, count: number): number {
  let hash = 2166136261;
  for (const char of seed) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % count;
}

export function russianYears(years: number): string {
  const lastTwo = years % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return `${years} лет`;
  const last = years % 10;
  if (last === 1) return `${years} год`;
  if (last >= 2 && last <= 4) return `${years} года`;
  return `${years} лет`;
}

function birthdayCopy(
  name: string,
  key: string
): { title: string; message: string } {
  const variants = [
    "Сегодня дедлайн только один — принимать поздравления. Желаем больше «хочу», меньше «надо» и людей, с которыми тепло и легко.",
    "Сегодня все хорошие слова — тебе. Пусть в календаре всегда находится время для любимых людей, больших планов и маленьких радостей.",
    "Пусть работа радует, планы сбываются, а жизнь приятно удивляет. Желаем поводов улыбаться чаще, чем обновлять рабочие чаты!",
  ];
  return {
    title: `С днём рождения, ${name}!`,
    message: variants[stableChoice(key, variants.length)],
  };
}

function anniversaryCopy(
  name: string,
  years: number,
  key: string
): { title: string; message: string } {
  const variants = [
    "Ещё одна глава нашей общей истории. Спасибо, что ты с нами! Пусть впереди будет больше любимых задач, смелых идей и побед, которые хочется отметить вместе.",
    "В этот день началась наша общая история. Спасибо, что ты часть команды! Желаем расти, пробовать новое и чаще говорить: «Вот это мы сделали!»",
    "Сегодня отмечаем наш с тобой рабочий день рождения. Спасибо за время вместе! Пусть следующий год принесёт интересные задачи и много поводов гордиться собой.",
  ];
  return {
    title: `${name} · ${russianYears(years)} с Change HR`,
    message: variants[stableChoice(key, variants.length)],
  };
}

export function celebrationsForDate(
  people: CelebrationPerson[],
  date: string
): TeamCelebration[] {
  if (!isValidCalendarDate(date)) return [];
  const [year, month, day] = date.split("-").map(Number);
  const items: TeamCelebration[] = [];
  for (const person of people) {
    const name =
      person.greetingName?.trim() || person.name?.trim() || "Коллега";
    if (person.birthdayMonth === month && person.birthdayDay !== null) {
      const birthdayDay = person.birthdayDay;
      if (
        birthdayDay === day ||
        (month === 2 && birthdayDay === 29 && day === 28 && !isLeapYear(year))
      ) {
        const key = celebrationKey(date, "birthday", person.userId);
        const copy = birthdayCopy(name, key);
        items.push({
          key,
          userId: person.userId,
          kind: "birthday",
          name,
          picture: person.picture,
          years: null,
          ...copy,
          dismissed: false,
        });
      }
    }
    if (!person.startedOn || !isValidCalendarDate(person.startedOn)) continue;
    const [, startMonth, startDay] = person.startedOn.split("-").map(Number);
    const observedAnniversaryDay =
      startMonth === 2 && startDay === 29 && !isLeapYear(year) ? 28 : startDay;
    const years = year - Number(person.startedOn.slice(0, 4));
    if (startMonth !== month || observedAnniversaryDay !== day || years < 1)
      continue;
    const key = celebrationKey(date, "anniversary", person.userId);
    const copy = anniversaryCopy(name, years, key);
    items.push({
      key,
      userId: person.userId,
      kind: "anniversary",
      name,
      picture: person.picture,
      years,
      ...copy,
      dismissed: false,
    });
  }
  return items.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === "birthday" ? -1 : 1;
    return a.name.localeCompare(b.name, "ru");
  });
}

export function applyDismissedKeys(
  items: TeamCelebration[],
  dismissedKeys: Set<string>
): TeamCelebration[] {
  return items.map(item => ({
    ...item,
    dismissed: dismissedKeys.has(item.key),
  }));
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}
